import { medimoQC } from "@medimo/core-api/internal/next";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { fetchUserAttributes, signOut } from "aws-amplify/auth";
import { useCallback } from "react";

type CognitoRawUserAttribute = {
  sub: string;
  name: string;
  email: string;
  email_verified: string;
  "custom:clinic_name": string;
  "custom:whereDidYouKnow"?: string;
};

function withTimeout<T>(promise: Promise<T>, timeout: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("タイムアウトしました。再度お試しください"));
    }, timeout);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

export const useCreateUserOnDB = (options?: UseMutationOptions) => {
  const { mutateAsync: patchUser } = medimoQC.useMutation("patch", "/v2/users");
  const { mutateAsync: postUser } = medimoQC.useMutation("post", "/v2/users");

  const registerUserOrUpdateOnDB = useCallback(async () => {
    const attr = (await fetchUserAttributes()) as CognitoRawUserAttribute;
    return patchUser({
      body: {
        clinic_name: attr["custom:clinic_name"],
        email: attr.email,
        name: attr.name,
      },
    }).catch(() => {
      return postUser({
        body: {
          cognito_sub: attr.sub,
          email: attr.email,
          is_terms_of_use_agreed: true,
          name: attr.name,
          clinic_name: attr["custom:clinic_name"],
          where_did_you_know: attr["custom:whereDidYouKnow"] || "",
          diarization_enabled: false,
          account_role: "user",
        },
      });
    });
  }, [patchUser, postUser]);
  const registerUserOrUpdateOnDBWithTimeout = useCallback(
    () => withTimeout(registerUserOrUpdateOnDB(), 8000),
    [registerUserOrUpdateOnDB],
  );

  return useMutation({
    mutationKey: ["registerUserOrUpdateOnDB"],
    mutationFn: registerUserOrUpdateOnDBWithTimeout,
    ...options,
    onError: async (error, variables, context, mutation) => {
      console.error(error);
      await signOut();
      options?.onError?.(error, variables, context, mutation);
    },
  });
};
